// src/pages/shops/AssignUsersPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Users, Plus, Trash2, ArrowLeft, AlertCircle,
  RefreshCw, Search, X, UserCircle, Loader2, Check,
} from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import {
  getShop,
  getShopAssignedUsers,
  assignUsersToShop,
  removeShopUser,
} from '../../services/api/shops';
import { getUsers } from '../../services/api/users';
import { getImageUrl } from '../../utils/imageUtils';
import styles from './AssignUsersPage.module.css';

// ─── Assign Modal ─────────────────────────────────────────────────────────────
const AssignUserModal = ({ shopId, assignedUserIds, onClose, onAssigned }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await getUsers({ limit: 200 });
        const users = (res.users || res || []).filter(u => !assignedUserIds.includes(u.id));
        setAllUsers(users);
        setFilteredUsers(users);
      } catch {
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const q = searchTerm.toLowerCase();
    setFilteredUsers(
      q ? allUsers.filter(u =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.UserRole?.name || '').toLowerCase().includes(q)
      ) : allUsers
    );
  }, [searchTerm, allUsers]);

  const toggleUser = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) return;
    setSaving(true);
    setError('');
    try {
      await assignUsersToShop(shopId, selectedIds);
      onAssigned();
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to assign users');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Assign Users to Shop</h3>
          <button className={styles.modalClose} onClick={onClose}><X size={18} /></button>
        </div>

        <div className={styles.modalBody}>
          {error && (
            <div className={styles.modalError}><AlertCircle size={14} />{error}</div>
          )}

          <div className={styles.searchInputWrapper}>
            <Search size={16} className={styles.searchIcon} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, role..."
              className={styles.searchInput}
              autoFocus
            />
            {searchTerm && (
              <button className={styles.clearSearch} onClick={() => setSearchTerm('')}>
                <X size={14} />
              </button>
            )}
          </div>

          {loading ? (
            <div className={styles.loadingState}><Loader2 size={20} className={styles.spinner} />Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className={styles.emptyState}>
              {searchTerm ? `No users found for "${searchTerm}"` : 'All users are already assigned to this shop'}
            </div>
          ) : (
            <div className={styles.userList}>
              {filteredUsers.map(user => {
                const isSelected = selectedIds.includes(user.id);
                return (
                  <label key={user.id} className={`${styles.userItem} ${isSelected ? styles.userItemSelected : ''}`}>
                    <div className={styles.userAvatar}>
                      {user.profilePicture ? (
                        <img src={getImageUrl(user.profilePicture)} alt={user.name} className={styles.avatarImg} />
                      ) : (
                        <UserCircle size={32} className={styles.avatarIcon} />
                      )}
                    </div>
                    <div className={styles.userInfo}>
                      <span className={styles.userName}>{user.name}</span>
                      <span className={styles.userMeta}>
                        <span>{user.email}</span>
                        {user.UserRole && <span className={styles.roleBadge}>{user.UserRole.name}</span>}
                      </span>
                    </div>
                    <div className={`${styles.checkbox} ${isSelected ? styles.checkboxSelected : ''}`}>
                      {isSelected && <Check size={12} />}
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleUser(user.id)}
                      className={styles.hiddenCheckbox}
                    />
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          {selectedIds.length > 0 && (
            <span className={styles.selectedCount}>{selectedIds.length} selected</span>
          )}
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
            <button
              className={styles.saveButton}
              onClick={handleAssign}
              disabled={saving || selectedIds.length === 0}
            >
              {saving ? <Loader2 size={14} className={styles.spinner} /> : <Plus size={14} />}
              Assign {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const AssignUsersPage = () => {
  const { shopId } = useParams();

  const [shop, setShop] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [shopRes, usersRes] = await Promise.all([
        getShop(shopId),
        getShopAssignedUsers(shopId),
      ]);
      setShop(shopRes);
      setUsers(usersRes);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredUsers = search.trim()
    ? users.filter(u =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        (u.UserRole?.name || '').toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const handleRemove = async (user) => {
    if (!window.confirm(`Remove "${user.name}" from this shop?`)) return;
    setDeletingId(user.id);
    try {
      await removeShopUser(shopId, user.id);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to remove user');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      key: 'name', header: 'User',
      render: (row) => (
        <div className={styles.userCell}>
          <div className={styles.userAvatar}>
            {row.profilePicture ? (
              <img src={getImageUrl(row.profilePicture)} alt={row.name} className={styles.avatarImg} />
            ) : (
              <UserCircle size={32} className={styles.avatarIcon} />
            )}
          </div>
          <div className={styles.userInfo}>
            <span className={styles.userName}>{row.name}</span>
            <span className={styles.userEmail}>{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'role', header: 'Role', width: '180px',
      render: (row) =>
        row.UserRole
          ? <span className={styles.roleBadge}>{row.UserRole.name}</span>
          : <span className={styles.na}>—</span>,
    },
    {
      key: 'phone', header: 'Phone', width: '160px',
      render: (row) => <span className={styles.phone}>{row.phone || '—'}</span>,
    },
    {
      key: 'isActive', header: 'Status', width: '100px', align: 'center',
      render: (row) => (
        <span className={`${styles.statusBadge} ${row.isActive ? styles.active : styles.inactive}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  const renderActions = (row) => (
    <div className={styles.rowActions}>
      <button
        className={`${styles.actionBtn} ${styles.deleteBtn}`}
        onClick={() => handleRemove(row)}
        disabled={deletingId === row.id}
        title="Remove from shop"
      >
        {deletingId === row.id
          ? <Loader2 size={14} className={styles.spinner} />
          : <Trash2 size={14} />}
      </button>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Assign Users${shop ? ` — ${shop.name}` : ''}`}
        description="Manage users who have access to this shop"
        icon={Users}
        actions={
          <div className={styles.headerActions}>
            <Link to="/shops" className={styles.backLink}>
              <ArrowLeft size={16} /> Back to Shops
            </Link>
            <button className={styles.primaryButton} onClick={() => setShowAssignModal(true)}>
              <Plus size={16} /> Assign User
            </button>
          </div>
        }
      />

      <FilterPanel
        filters={[]}
        values={{}}
        onChange={() => {}}
        show={false}
        onToggle={() => {}}
        searchConfig={{
          show: true,
          placeholder: 'Search by name, email, role...',
          value: search,
          onChange: setSearch,
        }}
      />

      {error && (
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={fetchData}><RefreshCw size={14} /></button>
        </div>
      )}

      <DataTable
        data={filteredUsers}
        columns={columns}
        actions={{ render: renderActions, width: '80px', frozen: true }}
        loading={loading}
        emptyMessage="No users assigned to this shop"
        emptyDescription="Click 'Assign User' to give users access to this shop"
        emptyIcon={<Users size={48} style={{ color: '#9ca3af' }} />}
        stickyHeader
      />

      {showAssignModal && (
        <AssignUserModal
          shopId={shopId}
          assignedUserIds={users.map(u => u.id)}
          onClose={() => setShowAssignModal(false)}
          onAssigned={fetchData}
        />
      )}
    </div>
  );
};

export default AssignUsersPage;